import { useQuery } from "@tanstack/react-query";
import { Navigate, Route, Routes } from "react-router-dom";
import { Button, PageError, PageLoading, Toaster } from "@lumika/ui";
import { adminApi } from "./api/admin";
import { ApiError } from "./api/client";
import { AdminShell } from "./layouts/AdminShell";
import { ChannelsPlaygroundLayout } from "./layouts/ChannelsPlaygroundLayout";
import { DealershipScope } from "./layouts/DealershipScope";
import { DealershipInboxLayout } from "./layouts/DealershipInboxLayout";
import { DealershipActivityPage } from "./pages/dealership/DealershipActivityPage";
import { DealershipAiPage } from "./pages/dealership/DealershipAiPage";
import { DealershipBillingPage } from "./pages/dealership/DealershipBillingPage";
import { DealershipCalendarPage } from "./pages/dealership/DealershipCalendarPage";
import { DealershipChannelsPage } from "./pages/dealership/DealershipChannelsPage";
import { DealershipIntegrationsPage } from "./pages/dealership/DealershipIntegrationsPage";
import { DealershipContactDetailPage } from "./pages/dealership/DealershipContactDetailPage";
import { DealershipContactsPage } from "./pages/dealership/DealershipContactsPage";
import { DealershipLeadDetailPage } from "./pages/dealership/DealershipLeadDetailPage";
import { DealershipInventoryImportsPage } from "./pages/dealership/DealershipInventoryImportsPage";
import { DealershipInventoryPage } from "./pages/dealership/DealershipInventoryPage";
import { DealershipOverviewPage } from "./pages/dealership/DealershipOverviewPage";
import { DealershipRooftopsPage } from "./pages/dealership/DealershipRooftopsPage";
import { DealershipTelephonyPage } from "./pages/dealership/DealershipTelephonyPage";
import { DealershipUsersPage } from "./pages/dealership/DealershipUsersPage";
import { TelephonyAccountDetailPage } from "./pages/dealership/TelephonyAccountDetailPage";
import { DealershipsLandingPage } from "./pages/DealershipsLandingPage";
import { NewDealershipPage } from "./pages/NewDealershipPage";
import { OutreachPage } from "./pages/outreach/OutreachPage";
import { OutreachProspectDetailPage } from "./pages/outreach/OutreachProspectDetailPage";
import { PlatformBillingPage } from "./pages/PlatformBillingPage";
import { SftpAccountsPage } from "./pages/SftpAccountsPage";
import { SftpBrowserPage } from "./pages/SftpBrowserPage";
import { ChannelsPlaygroundApiPage } from "./pages/channels-playground/ChannelsPlaygroundApiPage";
import { ChannelsPlaygroundWidgetPage } from "./pages/channels-playground/ChannelsPlaygroundWidgetPage";
import { SignInPage } from "./pages/SignInPage";

export function App() {
  const meQuery = useQuery({
    queryKey: ["admin", "me"],
    queryFn: adminApi.me,
    retry: false,
  });

  if (meQuery.isLoading) {
    return (
      <div className="grid min-h-screen place-items-center bg-background text-foreground">
        <PageLoading message="Loading lumika admin…" />
      </div>
    );
  }

  // A 401/403 means "signed out" — anything else (500, network blip) is
  // a transient failure that must not masquerade as the sign-in page.
  const authStatus =
    meQuery.error instanceof ApiError ? meQuery.error.status : null;
  if (meQuery.isError && authStatus !== 401 && authStatus !== 403) {
    return (
      <div className="grid min-h-screen place-items-center bg-background text-foreground">
        <PageError
          title="Couldn't load lumika admin"
          description={meQuery.error.message}
          action={
            <Button variant="outline" onClick={() => meQuery.refetch()}>
              Retry
            </Button>
          }
        />
      </div>
    );
  }

  if (!meQuery.data) {
    return <SignInPage />;
  }

  return (
    <>
      <Routes>
        <Route element={<AdminShell admin={meQuery.data.admin} />}>
          <Route index element={<Navigate to="/dealerships" replace />} />
          <Route path="dealerships">
            <Route index element={<DealershipsLandingPage />} />
            <Route path="new" element={<NewDealershipPage />} />
            <Route path=":dealershipId" element={<DealershipScope />}>
              <Route index element={<Navigate to="overview" replace />} />
              <Route path="overview" element={<DealershipOverviewPage />} />
              <Route path="inbox" element={<DealershipInboxLayout />}>
                <Route
                  path=":leadId"
                  element={<DealershipLeadDetailPage />}
                />
              </Route>
              <Route path="contacts">
                <Route index element={<DealershipContactsPage />} />
                <Route path=":contactId" element={<DealershipContactDetailPage />} />
              </Route>
              <Route path="calendar" element={<DealershipCalendarPage />} />
              {/* Legacy URL — keep the link working for bookmarks. */}
              <Route path="appointments" element={<Navigate to="../calendar" replace />} />
              <Route path="inventory" element={<DealershipInventoryPage />} />
              <Route
                path="inventory-imports"
                element={<DealershipInventoryImportsPage />}
              />
              {/* SFTP credentials + file browser are global, not
                  per-dealership — see /sftp at the top level. The
                  legacy dealerships/:id/sftp routes were removed when
                  the credential dropped its dealership_id FK. */}
              <Route path="sftp" element={<Navigate to="/sftp" replace />} />
              <Route path="rooftops" element={<DealershipRooftopsPage />} />
              <Route path="users" element={<DealershipUsersPage />} />
              <Route path="channels" element={<DealershipChannelsPage />} />
              <Route path="integrations" element={<DealershipIntegrationsPage />} />
              <Route path="tcr" element={<Navigate to="../telephony" replace />} />
              <Route path="telephony" element={<DealershipTelephonyPage />} />
              <Route
                path="telephony/:telephonyAccountId"
                element={<TelephonyAccountDetailPage />}
              />
              <Route path="ai" element={<DealershipAiPage />} />
              <Route path="billing" element={<DealershipBillingPage />} />
              <Route path="setup" element={<Navigate to="../channels" replace />} />
              <Route path="activity" element={<DealershipActivityPage />} />
            </Route>
          </Route>
          <Route path="outreach">
            <Route index element={<OutreachPage />} />
            <Route path=":prospectId" element={<OutreachProspectDetailPage />} />
          </Route>
          <Route path="billing" element={<PlatformBillingPage />} />
          <Route path="sftp" element={<SftpAccountsPage />} />
          <Route path="sftp/browser" element={<SftpBrowserPage />} />
          <Route path="channels-playground" element={<ChannelsPlaygroundLayout />}>
            <Route index element={<Navigate to="api" replace />} />
            <Route path="api" element={<ChannelsPlaygroundApiPage />} />
            <Route path="widget" element={<ChannelsPlaygroundWidgetPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/dealerships" replace />} />
        </Route>
      </Routes>
      <Toaster />
    </>
  );
}
